import {
    isProductDiagramInstruction,
    isSearchDiagramInstruction,
    ProductDiagramInstruction,
    SearchDiagramInstruction,
} from '../../stores/OdtExportStore';
import {BackendFilter, DataKey, isBackendFilter, isDataKey, SelectorTableColumnDef} from '../../stores/GeneralSelectorTableStore';
import {DateRange} from '../../stores/TimeIntervalStore';
import {HistoryItem} from '../../stores/MemoryBarStore';
import {DescriptiveKPIAdditions, isKPI, isKpiGroupKeyOrDescriptiveKpiAdditions, isKpiWithGroupKeys, KPI, KPIGroupKey} from '../../kpi';
import {isTableSortDirection, TableSort, TableSortDirection} from '../../types/Table';
import {AnalyticsTab} from '../../stores/store';
import {color} from 'csx';
import {isGeneralArray} from '../../types/Utility';
import {ReportError} from '../stores/ReportEditorStore';
import {Chart0Color, Chart1Color, Chart2Color, Chart3Color, BlackColor} from '../../styles/colors';

export enum PageLayout {
    PORTRAIT = 'portrait',
    LANDSCAPE = 'landscape',
}

export interface Report {
    id: number;
    name: string;
    lastChanged: number;
    blocks: ReportBuildingBlock[];
    currency?: string;
    layout: PageLayout;
    dates: NamedDateRange[];
    variables: string[];
}

export interface SerializedReport {
    id: number;
    lastChanged: number;
    name: string;
    currency?: string;
    layout: PageLayout;
    dates: NamedDateRange[];
    variables: string[];
    blocks: SerializedReportBuildingBlock[];
}

// @translateEnum(reportBuildingBlockType.)
export enum BuildingBlockType {
    TEXT = 'text',
    TABLE = 'table',
    DIAGRAM = 'diagram',
    DIAGRAM_TABLE = 'diagramTable',
    PAGE_BREAK = 'pageBreak',
}

export const tBuildingBlockTypeName = (buildingBlockType: BuildingBlockType) => {
    return `report:reportBuildingBlockType.${buildingBlockType}`;
};

export interface NamedDateRange {
    id: string;
    name: string;
}

export interface NamedDateRangeWithPreviewDateRange extends NamedDateRange {
    previewDateRange: DateRange;
}

export interface ReportEditing {
    editorIsActive: boolean;
    name: string;
    errors: ReportError[];
    blocks: ReportBuildingBlock[];
    id: number;
    layout: PageLayout;
    namedDateRanges: NamedDateRangeWithPreviewDateRange[];
}

export interface TextStyle {
    bold: boolean;
    italic: boolean;
    underlined: boolean;
    fontSize: number;
}

export interface ToggleableSelectorTableColumnDef extends SelectorTableColumnDef {
    isVisible: boolean;
}

export type SerializedReportBuildingBlock =
    | SerializedTextBuildingBlock
    | SerializedTableBuildingBlock
    | SerializedDiagramBuildingBlock
    | SerializedDiagramTableBuildingBlock
    | SerializedPageBreakBuildingBlock;

export interface SerializedReportBuildingBlockBase {
    blockType: BuildingBlockType;
    id: string;
}
export interface SerializedTextBuildingBlock extends SerializedReportBuildingBlockBase {
    textStyle: TextStyle;
    text: string;
}

export interface SerializedDiagramBuildingBlock extends SerializedReportBuildingBlockBase {
    searchDiagrams: SearchDiagramInstruction[];
    productDiagrams: ProductDiagramInstruction[];
    idDate: string;
    idDateCompare?: string;
}

export interface SerializedDiagramTableBuildingBlock extends SerializedReportBuildingBlockBase {
    searchDiagrams: SearchDiagramInstruction[];
    productDiagrams: ProductDiagramInstruction[];
    columns: DataKey[];
    idDate: string;
    idDateCompare?: string;
}

export interface SerializedTableBuildingBlock extends SerializedReportBuildingBlockBase {
    limit: number;
    tableMode: 'search' | 'product';
    search: string;
    sortAttribute: KPI | KPIGroupKey | DescriptiveKPIAdditions;
    sortDirection: TableSortDirection;
    filters: BackendFilter[];
    channel: string;
    columns: DataKey[];
    idDate: string;
}

export interface SerializedPageBreakBuildingBlock extends SerializedReportBuildingBlockBase {}

export type ReportBuildingBlock = TextBuildingBlock | TableBuildingBlock | DiagramBuildingBlock | DiagramTableBuildingBlock | PageBreakBuildingBlock;

export interface TextBuildingBlock {
    blockType: BuildingBlockType.TEXT;
    id: string;
    textStyle: TextStyle;
    text: string;
}

export interface TableBuildingBlock {
    blockType: BuildingBlockType.TABLE;
    id: string;
    memoryItems: Array<Partial<HistoryItem>>;
    lineLimit: number;
    columns: ToggleableSelectorTableColumnDef[];
    idDate: string;
}

export interface DiagramBuildingBlock {
    blockType: BuildingBlockType.DIAGRAM;
    id: string;
    memoryItems: Array<Partial<HistoryItem>>;
    idDate: string;
    idDateCompare?: string;
}

export interface DiagramTableBuildingBlock {
    blockType: BuildingBlockType.DIAGRAM_TABLE;
    id: string;
    memoryItems: Array<Partial<HistoryItem>>;
    columns: ToggleableSelectorTableColumnDef[];
    idDate: string;
    idDateCompare?: string;
}

export interface PageBreakBuildingBlock {
    blockType: BuildingBlockType.PAGE_BREAK;
    id: string;
}

export interface MergedBuildingBlockInstructions {
    diagramInstruction?: {
        searchDiagrams: SearchDiagramInstruction[];
        productDiagrams: ProductDiagramInstruction[];
        compareRangeActive?: boolean;
    };
    tableInstruction?: {
        activeViews: AnalyticsTab[];
        searches: string[];
        channels: string[];
        sortings: TableSort[];
        filters: BackendFilter[];
    };
    textInstruction?: {
        text: string;
        textStyle: TextStyle;
    };
}

export const BUILDING_BLOCK_TYPES = [
    BuildingBlockType.TEXT,
    BuildingBlockType.TABLE,
    BuildingBlockType.DIAGRAM,
    BuildingBlockType.DIAGRAM_TABLE,
    BuildingBlockType.PAGE_BREAK,
];

export const BUILDING_BLOCK_TYPE_COLORS = {
    [BuildingBlockType.TEXT]: color(Chart0Color),
    [BuildingBlockType.TABLE]: color(Chart1Color),
    [BuildingBlockType.DIAGRAM]: color(Chart2Color),
    [BuildingBlockType.DIAGRAM_TABLE]: color(Chart3Color),
    [BuildingBlockType.PAGE_BREAK]: color(BlackColor),
};

type Input = any;

const isReportBuildingBlock = (value: Input): value is ReportBuildingBlock => {
    return value && Object.values(BuildingBlockType).indexOf(value.blockType) > -1 && value.id && typeof value.id === 'string';
};

export const isSerializedTextBuildingBlock = (value: Input): value is TextBuildingBlock => {
    return (
        value &&
        value.textStyle &&
        typeof value.textStyle.underlined === 'boolean' &&
        typeof value.textStyle.italic === 'boolean' &&
        typeof value.textStyle.bold === 'boolean' &&
        typeof value.textStyle.fontSize === 'number' &&
        typeof value.text === 'string' &&
        isReportBuildingBlock(value) &&
        value.blockType === BuildingBlockType.TEXT
    );
};

export const isSerializedPageBreakBlock = (value: Input): value is PageBreakBuildingBlock => {
    return value && value.blockType === BuildingBlockType.PAGE_BREAK && isReportBuildingBlock(value);
};

export const isSerializedTableBuildingBlock = (value: Input): value is SerializedTableBuildingBlock => {
    return (
        value &&
        typeof value.limit === 'number' &&
        (value.tableMode === 'search' || value.tableMode === 'product') &&
        (value.search || value.search === '') &&
        (isKPI(value.sortAttribute) || isKpiWithGroupKeys(value.sortAttribute) || isKpiGroupKeyOrDescriptiveKpiAdditions(value.sortAttribute)) &&
        isTableSortDirection(value.sortDirection) &&
        isGeneralArray(value.filters) &&
        !value.filters.some((filter: Input) => !isBackendFilter(filter)) &&
        typeof value.channel === 'string' &&
        isGeneralArray(value.columns) &&
        !value.columns.some((column: Input) => !isDataKey(column)) &&
        typeof value.idDate === 'string' &&
        value.blockType === BuildingBlockType.TABLE &&
        isReportBuildingBlock(value)
    );
};

export const isSerializedDiagramBuildingBlock = (value: Input): value is SerializedDiagramBuildingBlock => {
    return (
        value &&
        ((isGeneralArray(value.searchDiagrams) && !value.searchDiagrams.some((searchDiagram: Input) => !isSearchDiagramInstruction(searchDiagram))) ||
            (isGeneralArray(value.productDiagrams) &&
                !value.productDiagrams.some((productDiagram: Input) => !isProductDiagramInstruction(productDiagram)))) &&
        typeof value.idDate === 'string' &&
        value.blockType === BuildingBlockType.DIAGRAM &&
        isReportBuildingBlock(value)
    );
};

export const isSerializedDiagramTableBuildingBlock = (value: Input): value is SerializedDiagramTableBuildingBlock => {
    return (
        value &&
        ((isGeneralArray(value.searchDiagrams) && !value.searchDiagrams.some((searchDiagram: Input) => !isSearchDiagramInstruction(searchDiagram))) ||
            (isGeneralArray(value.productDiagrams) &&
                !value.productDiagrams.some((productDiagram: Input) => !isProductDiagramInstruction(productDiagram)))) &&
        isGeneralArray(value.columns) &&
        !value.columns.some((column: Input) => !isDataKey(column)) &&
        typeof value.idDate === 'string' &&
        value.blockType === BuildingBlockType.DIAGRAM_TABLE &&
        isReportBuildingBlock(value)
    );
};

export interface DateRangeWithId {
    id: string;
    dateRange: DateRange;
}

export interface TextVariableWithValue {
    name: string;
    value: string;
}

export interface ReportGenerationModalPackage {
    report: Report;
    dateRangesWithId: DateRangeWithId[];
    textVariablesWithValue: TextVariableWithValue[];
}



// WEBPACK FOOTER //
// ./src/report/types/Report.ts